import React, { useState, useCallback } from 'react';
import { Form, Input, Select, Row, Col, Button, message, Alert, Switch } from "antd";
import { FacebookOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { isFacebookSDKLoaded, loginWithFacebookSdk } from '../../../Utils/facebook';
import { facebookBusinessLoginConfigId } from '../../../Config/facebookConfig';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';

const CredentialForm = ({ form, initialValues, isEdit = false }) => {
    const { t } = useTranslation();
    const { handleError } = useErrorHandler();
    const selectedChannel = Form.useWatch('channel_type', form);
    const [isConnected, setIsConnected] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);

    // Channel types - these should match your backend allowed values
    const channelTypes = [
        { value: 'facebook_page', label: t('facebook', { defaultValue: 'Facebook' }) },
        { value: 'instagram', label: `${t('instagram', { defaultValue: 'Instagram' })} (${t('coming_soon')})`, disabled: true },
        { value: 'whatsapp', label: `${t('whatsapp', { defaultValue: 'WhatsApp' })} (${t('coming_soon')})`, disabled: true },
        { value: 'telegram', label: `${t('telegram', { defaultValue: 'Telegram' })} (${t('coming_soon')})`, disabled: true },
    ];

    const handleFacebookLogin = useCallback(async () => {
        if (!isFacebookSDKLoaded) {
            message.error(t('facebook_sdk_not_loaded'));
            return;
        }

        setAuthLoading(true);
        loginWithFacebookSdk(async (response) => {
            try {
                if (response.authResponse?.accessToken) {
                    form.setFieldsValue({ short_lived_token: response.authResponse.accessToken });
                    setIsConnected(true);
                    message.success(t('auth_platform.connected', { defaultValue: 'Platform connected successfully' }));
                } else {
                    message.error(response.error || t('facebook_login_failed'));
                }
            } catch (error) {
                handleError(error);
            } finally {
                setAuthLoading(false);
            }
        }, facebookBusinessLoginConfigId);
    }, [form, handleError, t]);

    const isPlatformAuth = ['facebook_page', 'instagram'].includes(selectedChannel);

    return (
        <Form form={form} layout="vertical" initialValues={initialValues}>
            <Row gutter={16}>
                <Col xs={24}>
                    <Form.Item
                        label={t('channel_type')}
                        name="channel_type"
                        rules={[{ required: true, message: t('please_select_channel_type') }]}
                    >
                        <Select
                            placeholder={t('select_channel_type')}
                            options={channelTypes}
                            disabled={isEdit}
                            onChange={() => {
                                setIsConnected(false);
                                form.setFieldsValue({ short_lived_token: undefined });
                            }}
                        />
                    </Form.Item>
                </Col>

                {isEdit && (
                    <Col xs={24}>
                        <Form.Item
                            label={t('name')}
                            name="name"
                            rules={[
                                { max: 255, message: t('name_too_long') }
                            ]}
                        >
                            <Input placeholder={t('enter_credential_name')} />
                        </Form.Item>
                    </Col>
                )}

                {/* Platform Authentication for Facebook/Instagram */}
                {isPlatformAuth && (
                    <Col xs={24}>
                        <Form.Item
                            label={isEdit ? t('refresh_token') : t('authentication')}
                            required={!isEdit}
                        >
                            {isConnected ? (
                                <Alert
                                    message={t('connected')}
                                    description={t('token_acquired_ready_to_save', { defaultValue: 'Token acquired. You can now save the credential.' })}
                                    type="success"
                                    showIcon
                                    icon={<CheckCircleOutlined />}
                                    action={
                                        <Button size="small" type="text" onClick={() => {
                                            setIsConnected(false);
                                            form.setFieldsValue({ short_lived_token: undefined });
                                        }}>
                                            {t('reset')}
                                        </Button>
                                    }
                                />
                            ) : (
                                <Button
                                    type="primary"
                                    icon={<FacebookOutlined />}
                                    onClick={handleFacebookLogin}
                                    loading={authLoading}
                                    block
                                >
                                    {t('connect_with_facebook', { defaultValue: 'Connect with Facebook' })}
                                </Button>
                            )}
                        </Form.Item>
                        {/* Hidden field to store the token */}
                        <Form.Item
                            name="short_lived_token"
                            rules={!isEdit ? [{ required: true, message: t('please_authenticate_first', { defaultValue: 'Please authenticate first' }) }] : []}
                            hidden
                            style={{ margin: 0 }}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                )}
            </Row>
        </Form>
    );
};

export default CredentialForm;
